import React, { useMemo, useState } from 'react'
import { IoArrowDownSharp, IoArrowUpSharp, IoCheckmarkSharp, IoCloseSharp } from 'react-icons/io5'
import styled from 'styled-components'
import { media } from 'utils/media'
import { useTrackVisibility } from 'utils/useTrackVisibility'
import { Button } from 'lib/Button/Button'
import { StarRating } from 'lib/StarRating'
import { Text } from 'lib/Text'
import { useCurrentBrand } from 'modules/brands/show/context/CurrentBrandProvider'

const Container = styled.div`
  grid-gap: 24px;
  padding: 32px;
  margin-bottom: 24px;
  border: var(--border);
  border-color: var(--neutral-20);

  align-items: center;
  display: flex;
  flex-wrap: wrap;

  ${media.tabletDown} {
    padding: 24px;
  }
`

const Header = styled.header`
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex: 1;
  flex-wrap: wrap;
  gap: 16px;
`

const Rating = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  flex: 1;
  gap: 8px;
`

const UpIcon = styled(IoArrowUpSharp)`
  margin-right: 6px;
  position: relative;
  top: 2px;
`

const DownIcon = styled(IoArrowDownSharp)`
  margin-right: 6px;
  position: relative;
  top: 2px;
`

const Footer = styled.footer`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  flex: 1;
  gap: 16px;
`

const Buttons = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`

const SmallButton = styled(Button)`
  min-width: 60px;
  svg {
    margin-right: 5px;
    position: relative;
    top: -0.5px;
  }
`

export const HARD_CODED_RATINGS: {
  [key: string]: number
} = {
  'air-up': 4.7,
  'prose': 5.0,
  'hismile': 4.6,
  'skims': 5.0,
  'hiyo': 5.0
}

export const BrandsOwnRating = ({ brandRating }: { brandRating: number }) => {
  const { brand } = useCurrentBrand()
  const [dismissed, setDismissed] = useState(false)
  const { ref: visibilityRef } = useTrackVisibility<HTMLDivElement>({
    seen: 'Brands Own Rating Seen',
    seenLong: 'Brands Own Rating Read',
    payload: {
      brandId: brand.id,
      brandTitle: brand.title,
      brandSlug: brand.slug,
      brandRating
    }
  })

  const trackClick = (isUseful: boolean) => {
    window.analytics.track('Brands Own Rating Evaluated', {
      isUseful,
      brandId: brand.id,
      brandTitle: brand.title,
      brandSlug: brand.slug,
      brandRating
    })
  }

  const onYesClick = () => {
    setDismissed(true)
    trackClick(true)
  }

  const onNoClick = () => {
    setDismissed(true)
    trackClick(false)
  }

  const formattedUrl = useMemo((): string | null => {
    if (brand.webUrl) {
      try {
        return new URL(brand.webUrl).hostname.replace('www.', '')
      } catch {
        return brand.webUrl
      }
    } else {
      return null
    }
  }, [brand.webUrl])

  if (!brand.reviewSummary?.averageRating) {
    return null
  }

  const higher = brand.reviewSummary.averageRating - brandRating < 0
  const lower = brand.reviewSummary.averageRating - brandRating > 0
  const muchHigher = brand.reviewSummary.averageRating - brandRating < -1.5

  return (
    <Container ref={visibilityRef}>
      <Header>
        <Rating>
          <Text size="p" weight="semi">
            {brandRating}
          </Text>
          <StarRating stars={brandRating} starSize="small" />
        </Rating>

        <Text size="ps" weight="semi">
          {higher && (
            <>
              <UpIcon color={muchHigher ? 'var(--red-60)' : 'var(--neutral-100'} />
              {higher && 'Higher'}
            </>
          )}
          {lower && (
            <>
              <DownIcon color="var(--neutral-100)" />
              {lower && 'Lower'}
            </>
          )}
          {formattedUrl && <> on {formattedUrl}</>}
        </Text>
      </Header>
      <Text size="ps" color="neutral-60">
        Reviews on {brand.title}’s own website have a {muchHigher && 'much '}higher average rating compared to reviews on Thingtesting.
      </Text>

      <Footer>
        <Text size="ps" weight="semi">
          Was this useful?
        </Text>

        {!dismissed ? (
          <Buttons>
            <SmallButton onClick={onYesClick} startIcon={<IoCheckmarkSharp />} size="small" label="Yes" />
            <SmallButton onClick={onNoClick} startIcon={<IoCloseSharp />} size="small" label="No" />
          </Buttons>
        ) : (
          <Text size="ps" color="neutral-60">
            Thank you for letting us know!
          </Text>
        )}
      </Footer>
    </Container>
  )
}
